import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { organizationApi } from '../api/organizationApi';
import type { CreateOrganizationDTO, UpdateOrganizationDTO, OrganizationListResponse } from '../types';

export const organizationQueryKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationQueryKeys.all, 'list'] as const,
  list: (filters?: any) => [...organizationQueryKeys.lists(), filters] as const,
  details: () => [...organizationQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...organizationQueryKeys.details(), id] as const,
};

export const useOrganizations = () => {
  return useQuery({
    queryKey: organizationQueryKeys.list(),
    queryFn: organizationApi.list,
  });
};

export const useCreateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrganizationDTO) => organizationApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all });
      toast.success('Organization created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create organization');
    },
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateOrganizationDTO }) =>
      organizationApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all });
      toast.success('Organization updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update organization');
    },
  });
};

export const useUpdateOrganizationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      organizationApi.update(id, { is_active }),
    onMutate: async ({ id, is_active }) => {
      await queryClient.cancelQueries({ queryKey: organizationQueryKeys.list() });

      const previousData = queryClient.getQueryData(organizationQueryKeys.list());

      queryClient.setQueryData(organizationQueryKeys.list(), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          organizations: old.organizations.map((org: any) =>
            org.id === id ? { ...org, is_active } : org
          ),
        };
      });

      return { previousData };
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(organizationQueryKeys.list(), context.previousData);
      }
      toast.error('Failed to update organization status');
    },
    onSuccess: () => {
      toast.success('Organization status updated');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all });
    },
  });
};

export const useDeleteOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => organizationApi.delete(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: organizationQueryKeys.list() });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<OrganizationListResponse>(
        organizationQueryKeys.list()
      );

      // Optimistically remove the organization
      queryClient.setQueryData<OrganizationListResponse>(
        organizationQueryKeys.list(),
        (old) => {
          if (!old) return old;
          const deletedOrg = old.organizations.find(org => org.id === id);
          return {
            ...old,
            organizations: old.organizations.filter((org) => org.id !== id),
            total_count: old.total_count - 1,
            active_count: deletedOrg?.is_active 
              ? old.active_count - 1 
              : old.active_count,
          };
        }
      );

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(organizationQueryKeys.list(), context.previousData);
      }
      toast.error('Failed to delete organization');
    },
    onSuccess: () => {
      toast.success('Organization deleted successfully');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all });
    },
  });
};